/**
 * Чистый конструктор `LeadPayload`. Не лазит в `window`/`document` —
 * получает метаданные через параметры, чтобы оставаться тестируемым.
 */
import type { LeadPayload, LeadSource } from '@/lib/submitter';
import { readReferrer, readUtmExtras } from '@/lib/forms/readRequestMeta';

export type LeadFormFieldKey = 'name' | 'email' | 'phone' | 'company' | 'message';

export interface LeadFormValues {
  readonly name: string;
  readonly email: string;
  readonly phone: string;
  readonly company: string;
  readonly message: string;
  readonly consent: boolean;
}

export interface BuildLeadPayloadInput {
  readonly source: LeadSource;
  readonly locale: 'ru' | 'en';
  readonly fields: readonly LeadFormFieldKey[];
  readonly values: LeadFormValues;
  readonly path: string;
  readonly extras?: Record<string, unknown> | null;
  readonly referrer?: string;
  readonly utm?: Record<string, string>;
  readonly now?: Date;
}

function includeField(key: string, value: string): Record<string, string> {
  const trimmed = value.trim();
  return trimmed ? { [key]: trimmed } : {};
}

/**
 * Собирает `LeadPayload` из значений формы и метаданных. Поля, не
 * входящие в `fields`, отбрасываются. UTM и referrer можно передать
 * явно (для тестов) или не передавать — тогда подтянутся через
 * `readUtmExtras()` / `readReferrer()`.
 */
export function buildLeadPayload(input: BuildLeadPayloadInput): LeadPayload {
  const { source, locale, fields, values, path, extras, now } = input;
  const referrer = input.referrer ?? readReferrer();
  const utm = input.utm ?? readUtmExtras();
  const active = new Set(fields);

  return {
    source,
    locale,
    consent: values.consent,
    submittedAt: (now ?? new Date()).toISOString(),
    ...(active.has('name') ? includeField('name', values.name) : {}),
    ...(active.has('email') ? includeField('email', values.email) : {}),
    ...(active.has('phone') ? includeField('phone', values.phone) : {}),
    ...(active.has('company') ? includeField('company', values.company) : {}),
    ...(active.has('message') ? includeField('message', values.message) : {}),
    ...(referrer ? { referrer } : {}),
    extras: {
      path,
      ...utm,
      ...(extras ?? {}),
    },
  };
}
